import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, Calculator, Building2, FileText, Car,
  Image, BookOpen, Settings, Menu, X, Truck, Home,
  Bell, ChevronLeft, ChevronRight, LogOut
} from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../context/SettingsContext';

const NAV = [
  { to: '/admin',               icon: LayoutDashboard, label: 'Dashboard',     end: true },
  { to: '/admin/bookings',      icon: Calendar,        label: 'Bookings'  },
  { to: '/admin/estimates',     icon: Calculator,      label: 'Estimates' },
  { to: '/admin/b2b',           icon: Building2,       label: 'B2B Clients'   },
  { to: '/admin/invoices',      icon: FileText,        label: 'Invoices'  },
  { to: '/admin/car-purchases', icon: Car,             label: 'Car Purchases' },
  { to: '/admin/gallery',       icon: Image,           label: 'Gallery'   },
  { to: '/admin/blog',          icon: BookOpen,        label: 'Blog'      },
  { to: '/admin/settings',      icon: Settings,        label: 'Settings'  },
];

const BL = '#0D2B6B', YW = '#F5C400';

export default function AdminLayout({ onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed]   = useState(false);
  const [stats, setStats]           = useState(null);
  const { settings } = useSettings();

  // Load stats + poll every 45 seconds for live badge updates
  const loadStats = useCallback(() => {
    api.get('/api/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 45000); // refresh every 45s
    return () => clearInterval(interval);
  }, [loadStats]);

  const badges = {
    '/admin/bookings':  stats?.pending       || 0,
    '/admin/estimates': stats?.new_estimates || 0,
  };

  const logout = () => {
    if (onLogout) onLogout();
    else { localStorage.removeItem('apex_token'); window.location.reload(); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* ── SIDEBAR ── */}
      <aside style={{ backgroundColor: BL }} className={`
        fixed inset-y-0 left-0 z-50 flex flex-col text-white
        transition-all duration-200 ease-in-out
        ${mobileOpen ? 'w-56 translate-x-0' : 'w-56 -translate-x-full md:translate-x-0'}
        ${collapsed ? 'md:w-[68px]' : 'md:w-56'}
      `}>

        {/* Logo */}
        <div className="flex items-center justify-between px-3 py-4 border-b border-white/10 shrink-0 h-[60px]">
          <div className="flex items-center gap-3 overflow-hidden">
            {/* Show custom logo if set, else default truck icon */}
            {settings.logo_url ? (
              <img
                src={settings.logo_url}
                alt="Logo"
                className="w-9 h-9 object-contain rounded-lg shrink-0 bg-white/10 p-0.5"
                onError={e => e.target.style.display='none'}
              />
            ) : (
              <div style={{ backgroundColor: YW }} className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0">
                <Truck style={{ color: BL }} className="w-5 h-5" />
              </div>
            )}
            <div className={`transition-all duration-200 overflow-hidden ${collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
              <div className="font-black text-base leading-none whitespace-nowrap">
                {settings.company_name?.split(' ')[0] || 'APEX'}
              </div>
              <div className="text-[9px] text-white/40 tracking-widest uppercase whitespace-nowrap">Admin Panel</div>
            </div>
          </div>
          <button
            onClick={() => { setCollapsed(c => !c); setMobileOpen(false); }}
            className="hidden md:flex p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors shrink-0"
          >
            {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setMobileOpen(false)} className="md:hidden p-1.5 rounded-lg bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, icon: Icon, label, end }) => {
            const badge = badges[to] || 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-2.5 py-2.5 rounded-xl transition-all relative group overflow-hidden
                  ${isActive ? 'bg-white/15 font-semibold' : 'text-white/60 hover:bg-white/10 hover:text-white'}`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className="w-5 h-5 shrink-0" style={isActive ? { color: YW } : {}} />
                    <span className={`text-sm whitespace-nowrap transition-all duration-200 ${collapsed ? 'w-0 opacity-0 overflow-hidden' : 'opacity-100'}`}>
                      {label}
                    </span>
                    {badge > 0 && (
                      <span className={`bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 transition-all
                        ${collapsed ? 'absolute top-1.5 right-1' : 'ml-auto'}`}>
                        {badge}
                      </span>
                    )}
                    {/* Tooltip when collapsed */}
                    {collapsed && (
                      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
                        {label}
                        {badge > 0 && <span className="ml-1.5 bg-red-500 text-white rounded-full px-1.5 py-0.5 text-[9px] font-bold">{badge}</span>}
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-2 space-y-1.5 border-t border-white/10 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-3 px-2.5 py-2.5 rounded-xl font-bold hover:opacity-90 transition-all overflow-hidden"
            style={{ backgroundColor: YW, color: BL }}
          >
            <Home className="w-5 h-5 shrink-0" />
            <span className={`text-sm whitespace-nowrap transition-all duration-200 ${collapsed ? 'w-0 opacity-0 overflow-hidden' : 'opacity-100'}`}>
              View Website
            </span>
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-white/40 hover:bg-red-500/20 hover:text-red-400 transition-colors overflow-hidden"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span className={`text-sm whitespace-nowrap transition-all duration-200 ${collapsed ? 'w-0 opacity-0 overflow-hidden' : 'opacity-100'}`}>
              Logout
            </span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── MAIN CONTENT ── */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ${collapsed ? 'md:ml-[68px]' : 'md:ml-56'}`}>

        {/* Top header bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 flex items-center justify-between px-4 h-[60px]">
          <button onClick={() => setMobileOpen(true)} className="md:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 ml-auto">
            {/* New estimates alert */}
            {stats?.new_estimates > 0 && (
              <Link
                to="/admin/estimates"
                className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-amber-100 transition-colors"
              >
                <Bell className="w-3.5 h-3.5" />
                {stats.new_estimates} new {stats.new_estimates === 1 ? 'estimate' : 'estimates'}
              </Link>
            )}
            {/* Pending bookings alert */}
            {stats?.pending > 0 && (
              <Link
                to="/admin/bookings"
                className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-blue-100 transition-colors"
              >
                <Calendar className="w-3.5 h-3.5" />
                {stats.pending} pending
              </Link>
            )}
            {/* Admin avatar */}
            <div style={{ backgroundColor: BL }} className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" title={localStorage.getItem('apex_admin_name') || 'Admin'}>
              {(localStorage.getItem('apex_admin_name') || 'A')[0].toUpperCase()}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}